import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateUserRequest, UpdateUserRequest } from 'api/Identity/Types/AddEditUser'
import { UserResponse } from 'api/Identity/Types/UserResponse'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { AxiosError } from 'axios'
import AddButton from 'components/AddButton/AddButton'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Modal } from 'components/CustomModal'
import { FilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { pageableSettings } from 'store/grid/grid.slice'
import {
  fetchDateFormatAction,
  fetchDepartmentsAction,
  fetchRolesAction,
  fetchTeamsAction,
  fetchTickmillCompaniesAction,
  fetchUserKinds,
  fetchUserStatuses,
} from 'store/identity/identity.actions'
import { instanceOfAxiosError, useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery } from 'utils/QueryHelpers'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AddEditUserFormValues } from './AddEditUserForm'
import { AddEditUserTabs } from './AddEditUserTabs'
import { normalizeUsersSearchQuery } from './normalizer'
import userColumns from './userColumns'

const useFilter = () => {
  const identityApiClient = useApiClient(IdentityApiClient)

  const isFirstRun = useRef(true)
  const [users, setUsers] = useState<UserResponse[]>([])
  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )
  const [totalResults, setTotalResults] = useState<number>(0)
  const [isLoadingFilter, setLoading] = useState<boolean>(false)

  const getQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeUsersSearchQuery(filterDescriptor))
  }, [])

  const fetchFilter = async () => {
    setLoading(true)
    const searchQuery = getQuery(filter)
    const pageIndex = 1
    const pageSize = paginationState.take

    const response = await identityApiClient.getDetailedAdministrationUsers({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    if (response?.items) {
      setUsers(response.items)
      setPaginationState({
        skip: 0,
        take: pageSize,
      })
      setTotalResults(response.totalResults)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }

    const timeoutId = setTimeout(() => fetchFilter(), 500)
    return () => clearTimeout(timeoutId)
  }, [filter.filters]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoadingFilter,
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    users,
    setUsers,
    fetchFilter,
  }
}

const AdministrationUsers = (): JSX.Element => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)

  const [submitValues, setSubmitValues] = useState<AddEditUserFormValues>()
  const [isForceModalVisible, setForceModalVisible] = useState(false)
  const [auditLogModal, setAuditLogModal] = useState<{ visible: boolean; data?: UserResponse }>({
    visible: false,
  })

  const departments = useAppSelector((state) => state.identity.departments)
  const kinds = useAppSelector((state) => state.identity.userKinds)
  const statuses = useAppSelector((state) => state.identity.userStatuses)
  const teams = useAppSelector((state) => state.identity.teams) ?? []
  const roles = useAppSelector((state) => state.identity.roles)
  const tickmillCompanies = useAppSelector((state) => state.identity.tickmillCompanies)
  const languages = useAppSelector((state) => state.clients.language)
  const dateFormat = useAppSelector((state) => state.identity.dateFormat)

  const setToast = useContext(ToastContext)

  const [isLoading, setLoading] = useState<boolean>(true)
  const [showAddEditUserModal, setAddEditUserModal] = useState<{
    user?: UserResponse
    visible: boolean
  }>({
    visible: false,
  })

  const dispatch = useAppDispatch()

  const {
    filter,
    setFilter,
    paginationState,
    setPaginationState,
    setTotalResults,
    totalResults,
    users,
    setUsers,
    fetchFilter,
  } = useFilter()

  const getQuery = useCallback((filterDescriptor: CompositeFilterDescriptor) => {
    return buildQuery(normalizeUsersSearchQuery(filterDescriptor))
  }, [])

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      await fetchUsers()
      if (isEmpty(dateFormat)) {
        await dispatch(fetchDateFormatAction(identityApiClient))
      }
      if (isEmpty(departments)) {
        await dispatch(fetchDepartmentsAction(identityApiClient))
      }
      if (isEmpty(statuses)) {
        await dispatch(fetchUserStatuses(identityApiClient))
      }
      if (isEmpty(kinds)) {
        await dispatch(fetchUserKinds(identityApiClient))
      }
      if (isEmpty(teams)) {
        await dispatch(fetchTeamsAction(identityApiClient))
      }
      if (isEmpty(roles)) {
        await dispatch(fetchRolesAction(identityApiClient))
      }
      if (isEmpty(tickmillCompanies)) {
        await dispatch(fetchTickmillCompaniesAction(identityApiClient))
      }
      if (isEmpty(languages)) {
        dispatch(fetchClientLanguagesAction(clientsApiClient))
      }
      setLoading(false)
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUsers = async () => {
    const pagination = new Pagination({
      skip: paginationState.skip,
      recordsPerPage: paginationState.take,
    })
    const response = await identityApiClient.getDetailedAdministrationUsers({
      pageSize: pagination.getRecordsPerPage(),
      pageIndex: pagination.getPageIndex(),
    })
    if (response?.items) {
      setUsers(response.items)
      setPaginationState(paginationState)
      setTotalResults(response.totalResults)
    }
  }

  const onSubmit = async (values: AddEditUserFormValues, overrideRegionCheck?: boolean) => {
    if (values.id) {
      const data: UpdateUserRequest = {
        id: values.id,
        dateFormatTypeId: values.dateFormatTypeId,
        departmentsIds: values.departmentsIds,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        roleIds: values.roleIds,
        statusId: values.statusId,
        teams: values.teams.map((x) => ({
          teamId: x.team?.id || null,
          languageId: x.language?.id || null,
          eligibleToClientAutoAssignment: x.eligibleToClientAutoAssignment,
          visibility: x.visibility,
        })),
        isLocal: values.isLocal,
        isAutoAssignmentPaused: values.isAutoAssignmentPaused,
        autoAssignmentPausedStart: values.autoAssignmentPausedStart,
        autoAssignmentPausedUntil: values.autoAssignmentPausedUntil,
      }
      try {
        await identityApiClient.updateUser(values.id, data, overrideRegionCheck ?? false)
      } catch (err: unknown) {
        const error = err as { response: AxiosError<{ code?: string }> }
        if (instanceOfAxiosError(error?.response)) {
          const errorCode = error.response.response?.data?.code
          if (errorCode === 'same_region_team') {
            setSubmitValues(values)
            setForceModalVisible(true)
            return
          }
        }
        throw err
      }
    } else {
      const data: CreateUserRequest = {
        password: values.password || '',
        dateFormatTypeId: values.dateFormatTypeId,
        departmentsIds: values.departmentsIds || [],
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        roles: values.roleIds,
        teams: values.teams.map((x) => ({
          teamId: x.team?.id || null,
          languageId: x.language?.id || null,
          eligibleToClientAutoAssignment: x.eligibleToClientAutoAssignment,
          visibility: x.visibility,
        })),
        userStatusId: values.statusId || null,
        kindId: values.kindId,
        isLocal: values.isLocal,
        tickmillCompanyId: values.tickmillCompanyId || null,
      }
      await identityApiClient.addUser(data)
    }
    setToast(successToast(`User ${values.firstName} ${values.id ? 'updated' : 'created'}`))
    await fetchFilter()
    setSubmitValues(undefined)
    setAddEditUserModal({ visible: false, user: undefined })
    setForceModalVisible(false)
  }

  const handleOpenAddUserModal = () => {
    setSubmitValues(undefined)
    setAddEditUserModal({ visible: true })
  }

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    const response = await identityApiClient.getDetailedAdministrationUsers({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    if (response?.items) {
      setUsers(response.items)
    }
    setPaginationState({
      ...paginationState,
      take: pagination.getRecordsPerPage(),
      skip,
    })
  }

  const handleFilterChange = async (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
  }

  const handleResetFailedLogins = async (user: UserResponse) => {
    await identityApiClient.resetClientFailedLogins(user.id)
    setToast(successToast('Failed logins are now reset!'))
    await fetchFilter()
  }

  const setAuditLog = (user: UserResponse) => {
    setAuditLogModal({ data: user, visible: true })
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      {showAddEditUserModal.visible && !isForceModalVisible && (
        <Modal
          width={800}
          visible={showAddEditUserModal.visible && !isForceModalVisible}
          onClose={() => {
            setSubmitValues(undefined)
            setAddEditUserModal({ visible: false, user: undefined })
          }}
        >
          <AddEditUserTabs
            user={showAddEditUserModal.user}
            teams={teams}
            values={submitValues}
            languages={languages}
            roles={roles}
            dateFormat={dateFormat}
            departments={departments}
            tickmillCompanies={tickmillCompanies}
            onSubmit={onSubmit}
          />
        </Modal>
      )}
      {isForceModalVisible && (
        <ConfirmationModal
          contentText={`The user already has visibility to this country. Do you still want to proceed?`}
          yesButtonText='Yes'
          onYes={() => submitValues && onSubmit(submitValues, true)}
          onCancel={() => {
            setForceModalVisible(false)
          }}
          visible={isForceModalVisible}
        />
      )}
      {auditLogModal.visible && auditLogModal.data && (
        <ClientAreaAuditLogModal
          isVisible={auditLogModal.visible}
          onClose={() => setAuditLogModal({ visible: false })}
          administrationUserId={auditLogModal.data.id}
        />
      )}

      <Breadcrumbs crumbs={[{ title: 'Users', link: links.administrationUsers.url }]} />
      <Loading isLoading={isLoading}>
        <AddButton text='Add user' onClick={handleOpenAddUserModal} data-test='add-user-button' />
        <Grid
          filterable
          resizable
          data={users}
          filter={filter}
          take={paginationState.take}
          skip={paginationState.skip}
          total={totalResults}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          pageable={pageableSettings}
        >
          {userColumns
            .filter((c) => c.show !== false)
            .map((column, idx) => (
              <Column key={idx} {...column} />
            ))}
          <Column
            width={50}
            title=''
            filterCell={FilterCell}
            cell={(gridCellProps: GridCellProps) => (
              <td>
                <MoreButton
                  items={[
                    {
                      name: 'Edit',
                      'data-test': 'edit',
                      onClick: () => {
                        setSubmitValues(undefined)
                        setAddEditUserModal({
                          visible: true,
                          user: gridCellProps.dataItem,
                        })
                      },
                    },
                    {
                      name: 'Unlock',
                      'data-test': 'unlock',
                      onClick: () => handleResetFailedLogins(gridCellProps.dataItem),
                    },
                    {
                      name: 'Audit Log',
                      'data-test': 'audit_log',
                      onClick: () => setAuditLog(gridCellProps.dataItem),
                    },
                  ]}
                  data-test='more-button'
                />
              </td>
            )}
          />
        </Grid>
      </Loading>
    </>
  )
}

export default AdministrationUsers
